import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_USER_SETTINGS_NOTIFICATIONS } from '@/api/endpoints';
import { NOTIFICATION_SETTINGS_QUERY_KEY } from '@/api/queryKeys';
import type { NotificationSettingsResponse } from '@/types/notifications';
import { ErrorMessage, logRequestError } from '@/utils/errors';

interface Params {
  onError?: () => void;
}

export const useUpdateNotificationSettings = (
  userPublicId: string | undefined,
  { onError }: Params = {},
) => {
  const queryClient = useQueryClient();
  const queryKey = [...NOTIFICATION_SETTINGS_QUERY_KEY, userPublicId];

  const { mutate: updateSettings } = useMutation({
    mutationFn: (settings: NotificationSettingsResponse) =>
      apiClient.put(API_USER_SETTINGS_NOTIFICATIONS, settings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries({ queryKey });
      const previousSettings =
        queryClient.getQueryData<NotificationSettingsResponse>(queryKey);

      queryClient.setQueryData(queryKey, settings);

      return { previousSettings };
    },
    onError: (error, _settings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKey, context.previousSettings);
      }

      logRequestError(ErrorMessage.ChangeNotificationSettings, error);
      onError?.();
    },
  });

  return { updateSettings };
};
